<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref } from 'vue'

interface Props {
  initialClass?: string
  animatedClass?: string
  className?: string
  duration?: number
  delay?: number
  timingFunction?: string
  threshold?: number
  rootMargin?: string
}

const props = withDefaults(defineProps<Props>(), {
  initialClass: 'opacity-0 translate-y-8',
  animatedClass: 'opacity-100 translate-y-0',
  className: '',
  duration: 600,
  delay: 0,
  timingFunction: 'cubic-bezier(0.4, 0, 0.2, 1)',
  threshold: 0.2,
  rootMargin: '0px',
})

const elementRef = ref<HTMLElement | null>(null)
const hasAnimated = ref(false)
const isAnimating = ref(false)
let observer: IntersectionObserver | null = null
let animationTimeout: ReturnType<typeof setTimeout> | null = null

const willChangeValue = computed(() => {
  if (!isAnimating.value)
    return 'auto'
  const animated = props.animatedClass
  if (animated.includes('translate') || animated.includes('scale'))
    return 'transform, opacity'
  return 'opacity'
})

onMounted(() => {
  if (!elementRef.value)
    return

  observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting && !hasAnimated.value) {
          requestAnimationFrame(() => {
            hasAnimated.value = true
            isAnimating.value = true
            animationTimeout = setTimeout(() => {
              isAnimating.value = false
            }, props.duration + props.delay + 50)
            if (observer && elementRef.value)
              observer.unobserve(elementRef.value)
          })
        }
      })
    },
    { threshold: props.threshold, rootMargin: props.rootMargin },
  )

  observer.observe(elementRef.value)
})

onUnmounted(() => {
  observer?.disconnect()
  observer = null
  if (animationTimeout) {
    clearTimeout(animationTimeout)
    animationTimeout = null
  }
})
</script>

<template>
  <div
    ref="elementRef"
    class="transition-all"
    :class="[hasAnimated ? animatedClass : initialClass, className]"
    :style="{
      transitionDuration: `${duration}ms`,
      transitionDelay: `${delay}ms`,
      transitionTimingFunction: timingFunction,
      willChange: willChangeValue,
      contain: 'layout style paint',
    }"
  >
    <slot />
  </div>
</template>
